import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Link, Redirect } from 'react-router-dom';
import useQuery from '../../hooks/use-query';
import Loader from '../loader';
import Select from '../select';
import Paginator from '../paginator';
import api from '../../api';
import './indexed-search-list.css';

let searchUpdateTimeout = null;

const IndexedSearchActiveToggle = ({ indexedSearch }) => {
  const [state, setState] = useState(indexedSearch.active);
  const [isLoading, setIsLoading] = useState(false);

  const generateClassname = () => {
    let className = 'indexed-search-list_active-toggle';

    if (state) {
      className += ' indexed-search-list_active-toggle--active';
    }

    if (isLoading) {
      className += ' indexed-search-list_active-toggle--loading';
    }

    return className;
  };

  const toggle = () => {
    setIsLoading(true);
    api.indexedSearches.put(
      {
        id: indexedSearch.id,
        indexedSearch: {
          active: !state,
        },
      },
      () => {
        setState(!state);
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      },
    );
  };

  return (
    <span
      className={generateClassname()}
      onClick={e => {
        e.preventDefault();

        if (!isLoading) {
          toggle();
        }
      }}
    >
      <i className="indexed-search-list_active-toggle-icon material-icons">{state ? 'toggle_on' : 'toggle_off'}</i>
    </span>
  );
};

const IndexedSearchList = () => {
  const query = useQuery();
  const page = query.get('page') ? query.get('page') : 1;
  const itemsPerPage = query.get('itemsPerPage') ? query.get('itemsPerPage') : 25;
  const active = query.get('active') ? query.get('active') : null;
  const search = query.get('search') ? query.get('search') : '';
  const sortBy = query.get('sortBy') ? query.get('sortBy') : 'updatedAt';
  const sortDir = query.get('sortDir') ? query.get('sortDir') : 'DESC';
  const generateRouteParams = useCallback(params => {
    let routeParams = [];

    if (params.active || (active && !params.hasOwnProperty('active'))) {
      routeParams.push(`active=${params.active ? params.active : active}`);
    }

    if (params.search || (search && !params.hasOwnProperty('search'))) {
      routeParams.push(`search=${params.hasOwnProperty('search') ? params.search : search}`);
    }

    if (params.sortBy || (sortBy && !params.hasOwnProperty('sortBy'))) {
      routeParams.push(`sortBy=${params.hasOwnProperty('sortBy') ? params.sortBy : sortBy}`);
    }

    if (params.sortDir || (sortDir && !params.hasOwnProperty('sortDir'))) {
      routeParams.push(`sortDir=${params.hasOwnProperty('sortDir') ? params.sortDir : sortDir}`);
    }

    if (params.itemsPerPage || (itemsPerPage && !params.hasOwnProperty('itemsPerPage'))) {
      routeParams.push(`itemsPerPage=${params.hasOwnProperty('itemsPerPage') ? params.itemsPerPage : itemsPerPage}`);
    }

    if (params.page || (page && !params.hasOwnProperty('page'))) {
      routeParams.push(`page=${params.hasOwnProperty('page') ? params.page : page}`);
    }

    return `/recherches-indexees?${routeParams.join('&')}`;
  }, [active, search, sortBy, sortDir, itemsPerPage, page]);
  const [redirect, setRedirect] = useState(null);
  const [indexedSearches, setIndexedSearches] = useState([]);
  const [pages, setPages] = useState(0);
  const [nbResults, setNbResults] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const changeSort = by => {
    let dir = by === sortBy ? sortDir === 'DESC' ? 'ASC' : 'DESC' : 'ASC';

    setRedirect(generateRouteParams({sortBy: by, sortDir: dir, page: 1}));
  };

  useEffect(() => {
    const activeValues = {
      '0': false,
      '1': true,
      'all': ''
    };

    setRedirect(null);
    setIsLoading(true);
    api.indexedSearches.list(
      {
        search: search,
        page: page,
        items_per_page: itemsPerPage,
        sort_by: sortBy,
        sort_dir: sortDir,
        filters: {
          active: activeValues[active],
        },
      },
      data => {
        setIndexedSearches(data.items);
        setPages(data.pageCount);
        setNbResults(data.totalCount);
        setError(null);
        setIsLoading(false);
      },
      err => {
        setError(err);
        setIsLoading(false);
      },
    );
  }, [search, itemsPerPage, page, sortBy, sortDir, active]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <div className="indexed-search-list">
      <div className="indexed-search-list_nav">
        <div className="indexed-search-list_search">
          <input
            className="indexed-search-list_search-input"
            type="text"
            onChange={e => {
              let val = e.target.value;
              setSearchInput(val);
              clearTimeout(searchUpdateTimeout);
              searchUpdateTimeout = setTimeout(() => {
                setRedirect(generateRouteParams({search: val, page: 1}));
              }, 600);
            }}
            placeholder="Rechercher des liens"
            value={searchInput}
          />

          <Select
            name="active"
            options={[
              {
                label: 'Tous les statuts',
                value: 'all',
              },
              {
                label: 'Actif',
                value: '1',
              },
              {
                label: 'Inactif',
                value: '0',
              },
            ]}
            onChange={e => setRedirect(generateRouteParams({active: e.target.value, page: 1}))}
            label=""
            value={active || ''}
          />
        </div>

        <div className="indexed-search-list_pagination">
          <span className="indexed-search-list_count">Liens {(page - 1) * itemsPerPage + 1} à {(page - 1) * itemsPerPage + Number(itemsPerPage, 10)} sur {nbResults} résultats</span>
          <select
            className="indexed-search-list_items-per-page"
            id="ipp"
            onChange={e => setRedirect(generateRouteParams({itemsPerPage: e.target.value, page: 1}))}
            value={itemsPerPage}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {isLoading && <Loader />}

      {!isLoading && error && <p className="indexed-search-list_error">Une erreur s'est produite, essayez de réactualiser la page.</p>}

      {!isLoading && !error && (
        <div>
          <div className="indexed-search-list_table">
            <div className="indexed-search-list_header">
              <div
                className="indexed-search-list_header-cell indexed-search-list_header-cell--sortable indexed-search-list_cell--id"
                onClick={() => changeSort('id')}
              >
                ID
                {sortBy === 'id' && (
                  <i className="indexed-search-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

              <div
                className="indexed-search-list_header-cell indexed-search-list_header-cell--sortable indexed-search-list_cell--date"
                onClick={() => changeSort('updatedAt')}
              >
                Date
                {sortBy === 'updatedAt' && (
                  <i className="indexed-search-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

              <div
                className="indexed-search-list_header-cell indexed-search-list_header-cell--sortable indexed-search-list_cell--name"
                onClick={() => changeSort('searchText')}
              >
                Recherche
                {sortBy === 'searchText' && (
                  <i className="indexed-search-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

              <div
                className="indexed-search-list_header-cell indexed-search-list_header-cell--sortable indexed-search-list_cell--active"
                onClick={() => changeSort('active')}
              >
                Statut
                {sortBy === 'active' && (
                  <i className="indexed-search-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>
            </div>

            {indexedSearches.length > 0 && (
              <div>
                {indexedSearches.map(indexedSearch => (
                  <div
                    className="indexed-search-list_row"
                    key={indexedSearch.id}
                  >
                    <Link
                      className="indexed-search-list_cell indexed-search-list_cell--id"
                      title="Éditer la recherche"
                      to={`/recherches-indexees/${indexedSearch.id}/modifier`}
                    >
                      {indexedSearch.id}
                    </Link>

                    <Link
                      className="indexed-search-list_cell indexed-search-list_cell--date"
                      title="Éditer la recherche"
                      to={`/recherches-indexees/${indexedSearch.id}/modifier`}
                    >
                      {moment(indexedSearch.updatedAt).format('DD/MM/YYYY - HH:mm')}
                    </Link>

                    <Link
                      className="indexed-search-list_cell indexed-search-list_cell--name"
                      title="Éditer la recherche"
                      to={`/recherches-indexees/${indexedSearch.id}/modifier`}
                    >
                      {indexedSearch.searchText}
                    </Link>

                      <div className="indexed-search-list_cell indexed-search-list_cell--active-toggle">
                        <IndexedSearchActiveToggle indexedSearch={indexedSearch} />
                      </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Paginator
            currentPage={page}
            maxPage={pages}
            routePattern={generateRouteParams({page: '[page]'})}
          />
        </div>
      )}

      {indexedSearches.length === 0 && !isLoading && (
        <p className="indexed-search-list_no-result">Aucun résultat.</p>
      )}
    </div>
  );
};

export default IndexedSearchList;
